import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Skeleton } from '@solid/ui';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send, MoreVertical, Edit2, Trash2, X, Check, Archive, ArchiveRestore, ChevronDown, ChevronUp, Mail, Link2, CheckCircle, Download, ClipboardList, CalendarClock, Bell, BellOff, ArrowRightLeft, Flag } from 'lucide-react';
import { messagesApi, projectsApi, ReactionSummary, DiscussionSeenBy } from '@/lib/api';
import { discussionLabels } from '@/lib/discussion-labels';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toaster';
import { IS_STANDALONE } from '@/config/standalone';
import { Avatar } from '@/components/ui/Avatar';
import { StatusDropdown, DiscussionStatus } from '@/components/ui/StatusDropdown';
import { RichTextEditor, EVERYONE_MENTION_ID } from '@/components/ui/RichTextEditor';
import { resolveEmbeddedImages, withoutEmbedded } from '@/lib/embeds';
import { RichContent } from '@/components/ui/RichContent';
import { AttachmentDisplay } from '@/components/ui/AttachmentDisplay';
import { Reactions } from '@/components/ui/Reactions';
import { SeenBy, ReplySeenBy } from '@/components/discussions/SeenBy';
import { CreateTaskFromDiscussionModal } from '@/components/discussions/CreateTaskFromDiscussionModal';
import { useRealtimeInvalidation } from '@/hooks/useRealtime';
import { useFileAttachments } from '@/hooks/useFileAttachments';
import { useMentionableUsers } from '@/hooks/useMentionableUsers';
import { useDraft } from '@/hooks/useDraft';
import { formatRelativeTime, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { usePdfExport } from '@/hooks/usePdfExport';

interface ProjectMember {
  userId: string;
  role: string;
  canReceiveDiscussionEmails?: boolean;
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string;
  };
}

// Helper to transform raw reactions to ReactionSummary format
function transformReactions(reactions: any[] = [], currentUserId?: string): ReactionSummary[] {
  const emojiMap = new Map<string, { users: Array<{ id: string; name: string }>; hasReacted: boolean }>();
  
  for (const reaction of reactions) {
    const existing = emojiMap.get(reaction.emoji) || { users: [], hasReacted: false };
    existing.users.push({ id: reaction.user?.id || reaction.userId, name: reaction.user?.name || 'Unknown' });
    if (currentUserId && (reaction.userId === currentUserId || reaction.user?.id === currentUserId)) {
      existing.hasReacted = true;
    }
    emojiMap.set(reaction.emoji, existing);
  }

  const summary: ReactionSummary[] = [];
  for (const [emoji, data] of emojiMap.entries()) {
    summary.push({
      emoji,
      count: data.users.length,
      users: data.users,
      hasReacted: data.hasReacted,
    });
  }

  return summary;
}

function MessageActions({
  messageId,
  isAuthor,
  isAdmin,
  isArchived,
  onEdit,
  onDelete,
  onArchive,
  onUnarchive,
  onCopyLink,
  onExportPdf,
  onCreateTask,
  isImportant,
  onToggleImportant,
}: {
  messageId: string;
  isAuthor: boolean;
  isAdmin: boolean;
  isArchived: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onCopyLink: () => void;
  onExportPdf?: () => void;
  /** Agency admins only, root message only: turn the discussion into a task */
  onCreateTask?: () => void;
  isImportant?: boolean;
  /** This project's admins only: pin the discussion to the top of the list */
  onToggleImportant?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleCopyClick = () => {
    onCopyLink();
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      setOpen(false);
    }, 1000);
  };

  // Show menu if user is author, admin, or has export capability
  if (!isAuthor && !isAdmin && !onExportPdf) return null;
  
  return (
    <div className="relative no-print" ref={menuRef}>
      <button
        className="btn-icon"
        onClick={() => setOpen(!open)}
      >
        <MoreVertical className="w-4 h-4 text-grey-400" />
      </button>
      
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-10">
          {isAuthor && (
            <button
              onClick={() => { onEdit(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          )}
          <button
            onClick={handleCopyClick}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-success" />
                Link Copied
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4" />
                Copy Link
              </>
            )}
          </button>
          {onExportPdf && (
            <button
              onClick={() => { onExportPdf(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
            >
              <Download className="w-4 h-4" />
              Export as PDF
            </button>
          )}
          {onToggleImportant && (
            <button
              onClick={() => { onToggleImportant(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
            >
              <Flag className={cn('w-4 h-4', isImportant ? 'text-grey-400' : 'fill-warning text-warning')} />
              {isImportant ? 'Remove Important' : 'Mark as Important'}
            </button>
          )}
          {onCreateTask && (
            <button
              onClick={() => { onCreateTask(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
            >
              <ClipboardList className="w-4 h-4" />
              Create Task
            </button>
          )}
          {isAdmin && !isArchived && (
            <button
              onClick={() => { onArchive(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
            >
              <Archive className="w-4 h-4" />
              Archive Discussion
            </button>
          )}
          {isAdmin && isArchived && (
            <button
              onClick={() => { onUnarchive(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50"
            >
              <ArchiveRestore className="w-4 h-4" />
              Unarchive Discussion
            </button>
          )}
          {(isAuthor || isAdmin) && (
            <button
              onClick={() => { onDelete(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function MessageThread({
  threadId: threadIdProp,
  projectId: projectIdProp,
  typeSlug: typeSlugProp,
  onClose,
}: {
  threadId?: string;
  projectId?: string;
  typeSlug?: string;
  /** When provided, renders in overlay/takeover mode (modal, no back link). */
  onClose?: () => void;
} = {}) {
  const params = useParams();
  const projectId = projectIdProp ?? params.projectId;
  const projectRoles = useProjectRoles(projectId);
  const threadId = threadIdProp ?? params.threadId;
  const typeSlug = typeSlugProp ?? params.typeSlug;
  const overlay = !!onClose;
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const replyDraft = useDraft(`draft:reply:${threadId ?? ''}`);
  const [replyContent, setReplyContent] = useState(() => replyDraft.read());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [notifyUserIds, setNotifyUserIds] = useState<string[]>([]);
  const [notifyExpanded, setNotifyExpanded] = useState(false);
  /**
   * ON A PHONE THE COMPOSER STARTS AS ONE LINE.
   *
   * Expanded, it is an editor plus a toolbar that wraps to three rows, plus the
   * notify sentence, plus the button row — 300+px of a 650px screen, which left
   * no room to READ the discussion you came to read (Jesse, 2026-08-26). So on
   * small screens it collapses to a single tap target and opens on demand; once
   * opened it stays open for the visit, and a saved draft opens it too so
   * nobody loses sight of unsent words. Desktop is unchanged.
   */
  const [replyOpen, setReplyOpen] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= 640,
  );
  // Unsent words are never hidden: a restored draft opens the composer.
  useEffect(() => {
    const draft = replyDraft.read();
    if (draft && draft !== '<p></p>') setReplyOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);
  const [notifyInitialized, setNotifyInitialized] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [rescheduleValue, setRescheduleValue] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  // Parse hash on mount so the highlight is ready before first render
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(() => {
    const hash = window.location.hash;
    return hash.startsWith('#message-') ? hash.replace('#message-', '') : null;
  });

  // Get project to get organizationId and members
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  const organizationId = project?.organizationId || '';
  const isProjectArchived = !!project?.archivedAt;

  // Get project members from the fetched project data (for notification recipients)
  const projectMembers: ProjectMember[] = project?.members || [];

  // Get mentionable users for this project
  const { data: mentionableUsers = [], isLoading: mentionUsersLoading } = useMentionableUsers(projectId);

  // Get project notification defaults
  const { data: notificationDefaults = [] } = useQuery({
    queryKey: ['project-notification-defaults', projectId],
    queryFn: () => projectsApi.getNotificationDefaults(projectId!),
    enabled: !!projectId,
  });

  // Get the message/discussion thread
  const { data: message, isLoading } = useQuery({
    queryKey: ['message', threadId],
    queryFn: () => messagesApi.get(threadId!),
    enabled: !!threadId,
  });

  // Stale notification links can carry a reply id in the thread position.
  // Rendering a reply as a thread root lets users reply to it, which forks the
  // discussion — redirect to the real root with the reply anchored instead.
  useEffect(() => {
    if (!message?.parentId || !projectId) return;
    const rootPath = typeSlug
      ? `/projects/${projectId}/custom/${typeSlug}/${message.parentId}`
      : `/projects/${projectId}/discussions/${message.parentId}`;
    // The component doesn't remount on this navigation, so the mount-time hash
    // parsing won't run again — set the highlight target directly.
    setHighlightedMessageId(message.id);
    navigate(`${rootPath}#message-${message.id}`, { replace: true });
  }, [message?.parentId, message?.id, projectId, typeSlug, navigate]);

  // Users who have explicitly opted out of this discussion's notifications
  const optedOutUserIds: string[] = (message?.metadata as any)?.optedOutUserIds ?? [];

  // Compute locked participant IDs (discussion author + reply authors)
  // These users are always notified and cannot be unchecked.
  // Only include users who are still active project members — removed members
  // should not be locked into notification lists even if they previously replied.
  const lockedParticipantIds = useMemo(() => {
    if (!message) return new Set<string>();

    const memberIdSet = new Set(projectMembers.map((m) => m.userId));
    const participantIds = new Set<string>();

    // Add discussion author (only if still a project member)
    if (message.author?.id && message.author.id !== user?.id && memberIdSet.has(message.author.id)) {
      participantIds.add(message.author.id);
    }

    // Add all reply authors (only if still a project member)
    message.replies?.forEach((reply: any) => {
      if (reply.author?.id && reply.author.id !== user?.id && memberIdSet.has(reply.author.id)) {
        participantIds.add(reply.author.id);
      }
    });

    // Exclude users who have explicitly opted out
    optedOutUserIds.forEach(id => participantIds.delete(id));

    return participantIds;
  }, [message, user?.id, projectMembers, optedOutUserIds]);

  // Initialize notifyUserIds to include all thread participants
  // This ensures the discussion author and anyone who has replied gets notified
  useEffect(() => {
    if (!notifyInitialized && projectMembers.length > 0 && message) {
      // Start with locked participants (author + reply authors)
      const participantIds = new Set<string>(lockedParticipantIds);

      // Seed from persisted metadata notify list — filter to current members only.
      // Removed users may still appear in metadata if they were listed before removal;
      // excluding them here prevents "Unknown" entries and stops stale IDs being
      // re-submitted to the backend on the next reply.
      const memberIdSet = new Set(projectMembers.map((m) => m.userId));
      const metadataNotifyIds: string[] = (message.metadata as any)?.notifyUserIds ?? [];
      metadataNotifyIds.forEach((id: string) => {
        if (id !== user?.id && memberIdSet.has(id)) participantIds.add(id);
      });

      // Add mentioned users (these are not locked, but pre-selected)
      message.mentions?.forEach((m: any) => {
        if (m.userId && m.userId !== user?.id) {
          participantIds.add(m.userId);
        }
      });

      // Exclude users who have explicitly opted out
      optedOutUserIds.forEach(id => participantIds.delete(id));

      // Filter out users with notifications off
      const filtered = Array.from(participantIds).filter((id: string) => {
        const member = projectMembers.find((m) => m.userId === id);
        return member?.canReceiveDiscussionEmails !== false;
      });
      setNotifyUserIds(filtered);
      setNotifyInitialized(true);
    }
  }, [user?.id, notifyInitialized, projectMembers, message, lockedParticipantIds]);

  // File attachments for reply form
  const replyAttachments = useFileAttachments({
    organizationId,
    entityType: 'message',
    projectId,
  });

  // File attachments for edit mode (adding new attachments to existing messages)
  const editAttachments = useFileAttachments({
    organizationId,
    entityType: 'message',
    projectId,
  });

  // @everyone in the reply editor selects every member that can be notified
  // (the backend expands it too, but this keeps the visible notify list honest)
  const handleReplyMention = useCallback((userId: string) => {
    if (userId !== EVERYONE_MENTION_ID) return;
    const everyoneIds = projectMembers
      .filter(
        (m) =>
          m.userId !== user?.id &&
          m.canReceiveDiscussionEmails !== false &&
          !optedOutUserIds.includes(m.userId),
      )
      .map((m) => m.userId);
    setNotifyUserIds((prev) => [...new Set([...prev, ...everyoneIds])]);
  }, [projectMembers, user?.id, optedOutUserIds]);

  const replyMutation = useMutation({
    mutationFn: async (content: string) => {
      const reply = await messagesApi.createReply(threadId!, {
        content,
        notifyUserIds,
        attachmentIds: replyAttachments.collectAttachmentIds(content),
      });
      // Upload any staged files to the new reply
      if (replyAttachments.hasFiles && reply?.id) {
        await replyAttachments.uploadAll(reply.id);
      }
      return reply;
    },
    onSuccess: async () => {
      setReplyContent('');
      replyDraft.clear();
      replyAttachments.clearFiles();
      await queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      setNotifyInitialized(false); // Reset AFTER fresh data has arrived
    },
  });
  
  const updateMutation = useMutation({
    mutationFn: async ({ id, content, title }: { id: string; content: string; title?: string }) => {
      const updated = await messagesApi.update(id, {
        content,
        ...(title !== undefined && { title }),
        attachmentIds: editAttachments.collectAttachmentIds(content),
      });
      // Upload any new attachments
      if (editAttachments.hasFiles) {
        await editAttachments.uploadAll(id);
      }
      return updated;
    },
    onSuccess: () => {
      setEditingId(null);
      setEditContent('');
      setEditTitle('');
      editAttachments.clearFiles();
      queryClient.invalidateQueries({ queryKey: ['message', threadId] });
    },
  });
  
  const deleteMutation = useMutation({
    mutationFn: (id: string) => messagesApi.delete(id),
    onSuccess: (_, deletedId) => {
      setDeleteConfirmId(null);
      // If we deleted the main message, leave the thread view.
      if (deletedId === threadId) {
        queryClient.invalidateQueries({ queryKey: ['discussions', projectId] });
        if (overlay) onClose!();
        else navigate(typeSlug ? `/projects/${projectId}/custom/${typeSlug}` : `/projects/${projectId}/discussions`);
      } else {
        queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      }
    },
  });

  // Thread participation. The server drops opted-out users from every reply
  // path (including replies that arrive by email), so this toggle is the whole
  // story, not a UI-only checkbox.
  const selfParticipationMutation = useMutation({
    mutationFn: (optedOut: boolean) => messagesApi.updateSelfParticipation(threadId!, optedOut),
    onSuccess: (_data, optedOut) => {
      queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      toast({
        type: 'success',
        title: optedOut ? 'You left this thread' : 'You rejoined this thread',
        message: optedOut
          ? 'Replies will no longer email you. A direct @mention still will.'
          : 'Replies will email you again.',
      });
    },
    onError: () => toast({ type: 'error', title: "Couldn't update your notifications", message: 'Try again.' }),
  });

  // Agency members can take someone else off the thread (or put them back).
  const [removeConfirmUserId, setRemoveConfirmUserId] = useState<string | null>(null);
  const participationMutation = useMutation({
    mutationFn: ({ userId, optedOut }: { userId: string; optedOut: boolean }) =>
      messagesApi.updateParticipation(threadId!, userId, optedOut),
    onSuccess: (_data, { userId, optedOut }) => {
      queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      setNotifyUserIds((prev) => (optedOut ? prev.filter((id) => id !== userId) : [...new Set([...prev, userId])]));
      setRemoveConfirmUserId(null);
      const name = projectMembers.find((m) => m.userId === userId)?.user.name ?? 'They';
      toast({
        type: 'success',
        title: optedOut ? `${name} was taken off this thread` : `${name} is back on this thread`,
      });
    },
    onError: () => toast({ type: 'error', title: "Couldn't update notifications", message: 'Try again.' }),
  });

  const archiveMutation = useMutation({
    mutationFn: () => messagesApi.updateStatus(threadId!, 'archived'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['discussions', projectId] });
      if (overlay) onClose!();
      else navigate(typeSlug ? `/projects/${projectId}/custom/${typeSlug}` : `/projects/${projectId}/discussions`);
    },
  });

  // Important: highlighted and pinned to the top of the list, for everyone
  const importantMutation = useMutation({
    mutationFn: (important: boolean) => messagesApi.setImportant(threadId!, important),
    onSuccess: (_data, important) => {
      queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      queryClient.invalidateQueries({ queryKey: ['discussions', projectId] });
      toast({
        type: 'success',
        title: important ? 'Marked as Important' : 'No longer Important',
        message: important ? 'It’s pinned to the top of the list for everyone.' : 'It’s back in its usual place in the list.',
      });
    },
    onError: () => toast({ type: 'error', title: "Couldn't change Important", message: 'Try again.' }),
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => messagesApi.updateStatus(threadId!, 'open'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['discussions', projectId] });
    },
  });

  // Change the discussion status directly from the detail view (agency only).
  const statusMutation = useMutation({
    mutationFn: (status: DiscussionStatus) => messagesApi.updateStatus(threadId!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['discussions', projectId] });
    },
  });

  // Scheduled discussions: post now (null) or move to a new future time.
  const scheduleMutation = useMutation({
    mutationFn: (scheduledFor: string | null) => messagesApi.updateSchedule(threadId!, scheduledFor),
    onSuccess: () => {
      setRescheduling(false);
      setRescheduleValue('');
      queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      queryClient.invalidateQueries({ queryKey: ['discussions', projectId] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-discussions', projectId] });
    },
  });

  // If this message is a reply (has parentId), redirect to the parent discussion.
  // Skipped in overlay mode (cards only ever open top-level threads).
  useEffect(() => {
    if (message?.parentId && projectId && !overlay) {
      const path = typeSlug
        ? `/projects/${projectId}/custom/${typeSlug}/${message.parentId}`
        : `/projects/${projectId}/discussions/${message.parentId}`;
      navigate(path, { replace: true });
    }
  }, [message?.parentId, projectId, typeSlug, navigate]);

  // Scroll to hash anchor after message data loads (e.g. from email deep links)
  useEffect(() => {
    const { hash } = window.location;
    if (!hash || !message) return;

    // Defer scroll until the next frame so all replies are painted and
    // the layout is stable before we measure sticky header heights.
    const frameId = requestAnimationFrame(() => {
      const el = document.querySelector(hash);
      if (!el) return;

      // Sum heights of all sticky/fixed <header> elements to determine
      // how much space to reserve above the target when scrolling into view.
      // Summing heights (not bottom positions) is stable regardless of
      // current scroll state — secondary headers may not yet be at top: 0.
      const stickyOffset = Array.from(document.querySelectorAll<HTMLElement>('header')).reduce(
        (acc, node) => {
          const style = window.getComputedStyle(node);
          if (style.position !== 'fixed' && style.position !== 'sticky') return acc;
          return acc + node.getBoundingClientRect().height;
        },
        0,
      );

      // scroll-margin-top tells the browser to keep this much space above
      // the element when scrollIntoView is called — the native, reliable way
      // to account for sticky headers without manual scroll math.
      (el as HTMLElement).style.scrollMarginTop = `${stickyOffset + 16}px`;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Clear the highlight after the fade completes (3s = 0.5s scroll + 2.5s visible)
      setTimeout(() => setHighlightedMessageId(null), 3000);
    });

    return () => cancelAnimationFrame(frameId);
  }, [message]);

  // In the takeover modal the original message + replies scroll in a middle
  // pane (title pinned above, composer pinned below). Default it to the bottom
  // (latest reply) so the newest activity + composer are what you land on.
  const repliesScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (overlay && message && !window.location.hash) {
      const el = repliesScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [overlay, message?.id, message?.replies?.length]);

  // Listen for real-time replies
  useRealtimeInvalidation('new-reply', [['message', threadId!]]);

  // Read receipt: fire-and-forget on load and again as new replies arrive
  // while the thread is open (lastReadAt then covers them too). Never for
  // deleted or still-scheduled threads.
  useEffect(() => {
    if (!threadId || !message || message.deletedAt || message.scheduledFor) return;
    messagesApi.markRead(threadId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, message?.id, message?.replies?.length, message?.deletedAt, message?.scheduledFor]);

  // Live "Seen by" for agency admins: refetch when someone else records a read
  const seenByFilter = useCallback(
    (d: any) => d?.discussionId === threadId && d?.userId !== user?.id,
    [threadId, user?.id],
  );
  useRealtimeInvalidation('discussion-read', [['message', threadId!]], seenByFilter);
  
  const handleSubmitReply = (e: React.FormEvent) => {
    e.preventDefault();
    // Allow submission if there's text content OR if there are files attached
    const hasContent = replyContent.trim() && replyContent !== '<p></p>';
    if (hasContent || replyAttachments.hasFiles) {
      replyMutation.mutate(replyContent);
    }
  };
  
  const startEditing = (id: string, content: string, title?: string) => {
    // Stored embed srcs have expired; load the editor with current URLs, and
    // keep an embed even if its URL is momentarily missing.
    const target = id === threadId ? message : message?.replies?.find((r: any) => r.id === id);
    setEditingId(id);
    setEditContent(resolveEmbeddedImages(content, target?.attachments, false));
    setEditTitle(title || '');
  };
  
  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
    setEditTitle('');
    editAttachments.clearFiles();
  };

  const saveEdit = (id: string) => {
    if (editContent.trim()) {
      updateMutation.mutate({ id, content: editContent, title: editTitle || undefined });
    }
  };
  
  const handleCopyLink = async (messageId: string, isReply: boolean) => {
    const basePath = typeSlug
      ? `/projects/${projectId}/custom/${typeSlug}/${threadId}`
      : `/projects/${projectId}/discussions/${threadId}`;
    const url = isReply
      ? `${window.location.origin}${basePath}#message-${messageId}`
      : `${window.location.origin}${basePath}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const confirmDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  // Notifyees helper functions
  const toggleNotifyUser = (userId: string) => {
    // Someone who left the thread: only an agency member can put them back,
    // and it goes through the API. A local tick alone would be dropped by the
    // server on send, and the screen would have lied.
    if (optedOutUserIds.includes(userId)) {
      if (isAgencyMember) participationMutation.mutate({ userId, optedOut: false });
      return;
    }

    // A locked participant (author / reply author) can only be taken off by an
    // agency member, and that is a real removal, so it asks first.
    if (lockedParticipantIds.has(userId)) {
      if (isAgencyMember) setRemoveConfirmUserId(userId);
      return;
    }

    setNotifyUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllNotifyUsers = () => {
    // Select all users who can receive notifications
    const allUserIds = projectMembers
      .filter((m) => m.userId !== user?.id && m.canReceiveDiscussionEmails !== false)
      .map((m) => m.userId);
    setNotifyUserIds(allUserIds);
  };

  const selectNoneNotifyUsers = () => {
    // Keep locked participants, remove everyone else
    setNotifyUserIds(Array.from(lockedParticipantIds).filter((id) => {
      const member = projectMembers.find((m) => m.userId === id);
      return member?.canReceiveDiscussionEmails !== false;
    }));
  };

  // Get display names for selected notifyees
  const selectedNotifyNames = notifyUserIds
    .map((id) => projectMembers.find((m) => m.userId === id)?.user?.name || 'Unknown')
    .filter(Boolean);
  
  const { exportToPdf } = usePdfExport({ title: message?.title || 'Discussion' });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl" aria-busy>
        <Skeleton className="mb-4 h-6 w-32" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }
  
  const isMainAuthor = message?.author?.id === user?.id;

  // Resolve custom type label for back-link
  const typeConfig = typeSlug
    ? ((project?.settings as any)?.discussionTypes || []).find((dt: any) => dt.slug === typeSlug)
    : undefined;
  const backLinkLabel = typeSlug
    ? (typeConfig?.labelPlural || typeConfig?.label || typeSlug)
    : message?.project?.name
      ? `${message.project.name} ${discussionLabels((project as { settings?: unknown } | undefined)?.settings).plural}`
      : discussionLabels((project as { settings?: unknown } | undefined)?.settings).plural;

  // Staff on THIS project (can archive/unarchive discussions); admin for the
  // stricter surfaces (read receipts, create-task). Solid anywhere, a partner
  // agency on its own projects.
  const { isProjectStaff: isAgencyMember, isProjectAdmin: isAgencyAdminViewer } = projectRoles;
  const seenBy: DiscussionSeenBy[] = message?.seenBy ?? [];
  const linkedTasks: any[] = message?.linkedTasks ?? [];
  // Set when this discussion was converted from a task (see convertTaskToDiscussion).
  const convertedFrom = (message?.metadata as any)?.convertedFromTask as
    | { taskId: string; title: string; listId: string; mode: 'move' | 'copy' }
    | undefined;
  const isScheduled = !!message?.scheduledFor;
  const viewerHasLeft = !!user?.id && optedOutUserIds.includes(user.id);

  const isArchived = message?.discussionStatus === 'archived';

  if (message?.deletedAt) {
    return (
      <div className="max-w-4xl mx-auto">
        <Link
          to={typeSlug ? `/projects/${projectId}/custom/${typeSlug}` : `/projects/${projectId}/discussions`}
          className="inline-flex items-center gap-2 text-sm text-grey-500 hover:text-secondary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {backLinkLabel}
        </Link>
        <Card className="p-8 text-center">
          <p className="text-grey-500">This discussion was deleted.</p>
        </Card>
      </div>
    );
  }

  return (
    <div>
    <div className={overlay ? '' : 'max-w-4xl mx-auto'}>
      {/* Breadcrumb (route mode only — the takeover has its own close button) */}
      {!overlay && (
        <div className="flex items-center justify-between mb-6 no-print">
          <Link
            to={typeSlug ? `/projects/${projectId}/custom/${typeSlug}` : `/projects/${projectId}/discussions`}
            className="inline-flex items-center gap-2 text-sm text-grey-500 hover:text-secondary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {backLinkLabel}
          </Link>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center p-4',
            !IS_STANDALONE && 'top-[var(--solid-rail-height,56px)] lg:top-0 lg:left-[var(--solid-rail-width,52px)]',
          )}
        >
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">Delete Discussion?</h3>
            <p className="text-grey-500 text-sm mb-6">
              This action cannot be undone. {deleteConfirmId === threadId ? 'This will delete the entire thread.' : ''}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button 
                variant="danger" 
                onClick={() => confirmDelete(deleteConfirmId)}
                isLoading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Take-off-thread confirmation (agency): a real removal, so it asks
          first, and the focused button is Cancel. */}
      {removeConfirmUserId && (
        <div
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center p-4',
            !IS_STANDALONE && 'top-[var(--solid-rail-height,56px)] lg:top-0 lg:left-[var(--solid-rail-width,52px)]',
          )}
        >
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setRemoveConfirmUserId(null)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">
              Take {projectMembers.find((m) => m.userId === removeConfirmUserId)?.user.name ?? 'this person'} off this thread?
            </h3>
            <p className="text-grey-500 text-sm mb-6">
              They will stop getting emails when anyone replies, including replies sent by email.
              A direct @mention still reaches them. You can put them back from this list.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" autoFocus onClick={() => setRemoveConfirmUserId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                isLoading={participationMutation.isPending}
                onClick={() => participationMutation.mutate({ userId: removeConfirmUserId, optedOut: true })}
              >
                Take off thread
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Discussion Card - Contains everything */}
      <Card className={cn('pdf-export-root', overlay ? 'rounded-2xl shadow-2xl max-h-[85vh] flex flex-col' : 'mb-6')}>
        {/* Title Section - Most prominent (pinned in overlay, with a scroll shadow).
            Always shown in the modal so the status + actions have a home. */}
        {(message?.title || overlay) && (
          <div className={cn('flex items-start justify-between', overlay ? 'flex-shrink-0 relative z-10 pb-4 border-b border-grey-100 shadow-[0_8px_10px_-10px_rgba(15,23,42,0.25)]' : 'mb-6')}>
            <div className="flex-1 min-w-0 flex items-center gap-3">
              {editingId === threadId ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="text-2xl font-semibold text-secondary w-full px-3 py-1 rounded-lg border border-grey-300 bg-white focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none"
                  placeholder="Discussion title"
                  autoFocus
                />
              ) : (
                <h1 className={cn('text-2xl font-semibold', message?.title ? 'text-secondary' : 'text-grey-400')}>
                  {message?.title || 'Untitled Discussion'}
                </h1>
              )}
              {isArchived && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-grey-100 text-grey-600 rounded-full">
                  <Archive className="w-3 h-3" />
                  Archived
                </span>
              )}
              {message?.importantAt && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase tracking-wide bg-warning/15 text-secondary rounded-full whitespace-nowrap">
                  <Flag className="w-3 h-3 fill-warning text-warning" />
                  Important
                </span>
              )}
              {isScheduled && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-accent/10 text-accent rounded-full whitespace-nowrap">
                  <CalendarClock className="w-3 h-3" />
                  Scheduled
                </span>
              )}
            </div>
            {/* pr-11 on a phone: the takeover's floating close button sits at
                right-3 inside the card there, directly on top of this cluster
                (screenshot, 2026-08-27). Desktop puts the X outside the card,
                so it needs no room. */}
            <div className={cn('flex items-center gap-2 flex-shrink-0', overlay && 'pr-11 sm:pr-0')}>
              {/* In the modal, the status lives in the pinned header */}
              {overlay && isAgencyMember && message && (
                <div data-status-dropdown>
                  <StatusDropdown
                    status={(message.discussionStatus || 'open') as DiscussionStatus}
                    onChange={(newStatus) => statusMutation.mutate(newStatus)}
                  />
                </div>
              )}
              <MessageActions
                messageId={threadId!}
                isAuthor={isMainAuthor}
                isAdmin={isAgencyMember}
                isArchived={isArchived}
                onEdit={() => startEditing(threadId!, message?.content || '', message?.title || '')}
                onDelete={() => setDeleteConfirmId(threadId!)}
                onArchive={() => archiveMutation.mutate()}
                onUnarchive={() => unarchiveMutation.mutate()}
                onCopyLink={() => handleCopyLink(threadId!, false)}
                onExportPdf={exportToPdf}
                onCreateTask={isAgencyAdminViewer && !isScheduled ? () => setShowCreateTask(true) : undefined}
                isImportant={!!message?.importantAt}
                onToggleImportant={
                  isAgencyAdminViewer && !isScheduled && !isArchived && !isProjectArchived
                    ? () => importantMutation.mutate(!message?.importantAt)
                    : undefined
                }
              />
            </div>
          </div>
        )}

        {/* Scheduled banner: visible only to the author/admins (others 404) */}
        {isScheduled && (
          <div className="mb-6 rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 no-print">
            <div className="flex flex-wrap items-center gap-3">
              <CalendarClock className="w-4 h-4 text-accent flex-shrink-0" />
              <span className="text-sm text-secondary">
                Scheduled for <strong>{new Date(message!.scheduledFor).toLocaleString()}</strong> — only you and agency admins can see it until then.
              </span>
              <div className="flex items-center gap-2 ml-auto">
                {rescheduling ? (
                  <>
                    <input
                      type="datetime-local"
                      value={rescheduleValue}
                      min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                      onChange={(e) => setRescheduleValue(e.target.value)}
                      className="input py-1 text-sm"
                    />
                    <Button
                      size="sm"
                      disabled={!rescheduleValue || scheduleMutation.isPending}
                      onClick={() => scheduleMutation.mutate(new Date(rescheduleValue).toISOString())}
                    >
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRescheduling(false)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setRescheduling(true)}>
                      Reschedule
                    </Button>
                    <Button
                      size="sm"
                      isLoading={scheduleMutation.isPending}
                      onClick={() => scheduleMutation.mutate(null)}
                    >
                      Post now
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Provenance: this discussion used to be a task */}
        {convertedFrom && (
          <div className="mb-4 no-print">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-grey-200 bg-grey-50 px-3 py-1 text-xs text-grey-600">
              <ArrowRightLeft className="w-3.5 h-3.5 text-accent" />
              {convertedFrom.mode === 'copy' ? 'Copied from task' : 'Moved from task'}
              {convertedFrom.mode === 'copy' && projectId ? (
                <Link
                  to={`/projects/${projectId}/tasks/${convertedFrom.listId}?taskId=${convertedFrom.taskId}`}
                  onClick={() => { if (overlay) onClose!(); }}
                  className="font-medium text-accent hover:underline"
                >
                  {convertedFrom.title}
                </Link>
              ) : (
                <span className="font-medium text-secondary">{convertedFrom.title}</span>
              )}
            </span>
          </div>
        )}

        {/* Linked tasks (created from this discussion) — agency-admin payload */}
        {linkedTasks.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 no-print">
            {linkedTasks.map((task: any) => (
              <Link
                key={task.id}
                to={`/projects/${projectId}/tasks/${task.listId}?taskId=${task.id}`}
                onClick={() => { if (overlay) onClose!(); }}
                className="inline-flex items-center gap-1.5 rounded-full border border-grey-200 bg-grey-50 px-3 py-1 text-xs font-medium text-secondary hover:border-accent/40 hover:bg-accent/5 transition-colors"
              >
                <ClipboardList className="w-3.5 h-3.5 text-accent" />
                <span className="truncate max-w-[240px]">{task.title}</span>
                {task.statusConfig?.name && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] text-grey-500"
                    style={task.statusConfig?.color ? { color: task.statusConfig.color } : undefined}
                  >
                    ● {task.statusConfig.name}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* Scrolling pane in overlay: original message + replies */}
        <div ref={repliesScrollRef} className={overlay ? 'flex-1 min-h-0 overflow-y-auto -mx-6 px-6 pt-5' : 'contents'}>
        {/* Original message author and content */}
        <div className="flex gap-3 mb-6 pdf-message-item">
          <Avatar
            src={message?.author?.avatarUrl}
            name={message?.author?.name || 'User'}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-secondary">{message?.author?.name}</span>
              <span className="text-sm text-grey-400">
                {message?.createdAt && formatRelativeTime(message.createdAt)}
                {message?.editedAt && <span> (edited)</span>}
              </span>
              <button
                onClick={() => handleCopyLink(threadId!, false)}
                className="p-1 hover:bg-grey-100 rounded transition-colors no-print"
                title={copiedMessageId === threadId ? 'Link copied!' : 'Copy link'}
              >
                {copiedMessageId === threadId ? (
                  <CheckCircle className="w-3.5 h-3.5 text-success" />
                ) : (
                  <Link2 className="w-3.5 h-3.5 text-grey-400" />
                )}
              </button>
              {/* Change discussion status right from the detail view (agency only).
                  In the modal this moves to the pinned header, so hide it here. */}
              {!overlay && isAgencyMember && message && (
                <div className="ml-auto" data-status-dropdown>
                  <StatusDropdown
                    status={(message.discussionStatus || 'open') as DiscussionStatus}
                    onChange={(newStatus) => statusMutation.mutate(newStatus)}
                  />
                </div>
              )}
            </div>

            {editingId === threadId ? (
              <div className="space-y-3">
                <RichTextEditor
                  key="edit-mode"
                  content={editContent}
                  onChange={setEditContent}
                  minHeight="150px"
                  maxHeight="50vh"
                  enableAttachments={!!organizationId}
                  stagedFiles={editAttachments.stagedFiles}
                  onFilesAdded={editAttachments.addFiles}
                  onFileRemoved={editAttachments.removeFile}
                  onEmbedImage={editAttachments.embedImage}
                  onDemoteEmbed={editAttachments.addUploaded}
                  disabled={updateMutation.isPending}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={cancelEditing}>
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => saveEdit(threadId!)}
                    isLoading={updateMutation.isPending || editAttachments.isUploading}
                  >
                    <Check className="w-4 h-4" />
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <RichContent html={message?.content || ''} attachments={message?.attachments} className="text-[16px] leading-relaxed text-grey-700" />
                {message?.attachments && message.attachments.length > 0 && (
                  <AttachmentDisplay attachments={withoutEmbedded(message.attachments, message.content)} className="mt-4" />
                )}
              </>
            )}

            {/* Reactions for original message */}
            {!editingId && (
              <div className="mt-4 no-print">
                <Reactions
                  entityType="message"
                  entityId={threadId!}
                  initialReactions={transformReactions(message?.reactions, user?.id)}
                  onReactionsChange={() => queryClient.invalidateQueries({ queryKey: ['message', threadId] })}
                />
              </div>
            )}

            {/* Who has viewed this discussion — agency admins only (the API
                omits seenBy for everyone else, so this renders nothing) */}
            {isAgencyAdminViewer && seenBy.length > 0 && (
              <div className="mt-3">
                <SeenBy seenBy={seenBy} currentUserId={user?.id} />
              </div>
            )}
          </div>
        </div>

        {/* Replies Section - Continuous flow */}
        {message?.replies && message.replies.length > 0 && (
          <>
            <div className="border-t border-grey-200 pt-4 mt-4">
              <p className="text-sm text-grey-500 mb-4">{message.replies.length} {message.replies.length === 1 ? 'Reply' : 'Replies'}</p>
            </div>

            <div className="space-y-0">
              {message.replies.map((reply: any, index: number) => {
                const isReplyAuthor = reply.author?.id === user?.id;
                const isEditing = editingId === reply.id;
                const isLast = index === message.replies.length - 1;

                return (
                  <div
                    id={`message-${reply.id}`}
                    key={reply.id}
                    className={cn(
                      'flex gap-3 py-4 rounded-lg px-2 -mx-2 pdf-message-item',
                      !isLast && 'border-b border-grey-100',
                      highlightedMessageId === reply.id && 'msg-highlight',
                    )}

                  >
                    <Avatar
                      src={reply.author?.avatarUrl}
                      name={reply.author?.name || 'User'}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-secondary">{reply.author?.name}</span>
                          <span className="text-sm text-grey-400">
                            {formatRelativeTime(reply.createdAt)}
                            {reply.editedAt && <span> (edited)</span>}
                          </span>
                          <button
                            onClick={() => handleCopyLink(reply.id, true)}
                            className="p-1 hover:bg-grey-100 rounded transition-colors no-print"
                            title={copiedMessageId === reply.id ? 'Link copied!' : 'Copy link'}
                          >
                            {copiedMessageId === reply.id ? (
                              <CheckCircle className="w-3.5 h-3.5 text-success" />
                            ) : (
                              <Link2 className="w-3.5 h-3.5 text-grey-400" />
                            )}
                          </button>
                        </div>
                        <MessageActions
                          messageId={reply.id}
                          isAuthor={isReplyAuthor}
                          isAdmin={false}
                          isArchived={false}
                          onEdit={() => startEditing(reply.id, reply.content)}
                          onDelete={() => setDeleteConfirmId(reply.id)}
                          onArchive={() => {}}
                          onUnarchive={() => {}}
                          onCopyLink={() => handleCopyLink(reply.id, true)}
                        />
                      </div>

                      {isEditing ? (
                        <div className="space-y-3">
                          <RichTextEditor
                            key="edit-mode"
                            content={editContent}
                            onChange={setEditContent}
                            minHeight="100px"
                            maxHeight="50vh"
                            enableAttachments={!!organizationId}
                            stagedFiles={editAttachments.stagedFiles}
                            onFilesAdded={editAttachments.addFiles}
                            onFileRemoved={editAttachments.removeFile}
                            onEmbedImage={editAttachments.embedImage}
                            onDemoteEmbed={editAttachments.addUploaded}
                            disabled={updateMutation.isPending}
                          />
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={cancelEditing}>
                              <X className="w-4 h-4" />
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => saveEdit(reply.id)}
                              isLoading={updateMutation.isPending || editAttachments.isUploading}
                            >
                              <Check className="w-4 h-4" />
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <RichContent html={reply.content} attachments={reply.attachments} className="text-[15px] text-grey-700" />
                          {reply.attachments && reply.attachments.length > 0 && (
                            <AttachmentDisplay attachments={withoutEmbedded(reply.attachments, reply.content)} className="mt-3" />
                          )}
                          <div className="mt-2 no-print flex items-center justify-between gap-2">
                            <Reactions
                              entityType="message"
                              entityId={reply.id}
                              initialReactions={transformReactions(reply.reactions, user?.id)}
                              size="sm"
                            />
                            {/* Derived: seen by everyone whose lastReadAt covers this reply */}
                            {isAgencyAdminViewer && seenBy.length > 0 && (
                              <ReplySeenBy seenBy={seenBy} replyCreatedAt={reply.createdAt} currentUserId={user?.id} />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        </div>
        {/* end scrolling pane */}

        {/* Reply form - Part of the same card (hidden for archived projects
            and for scheduled threads — nobody can reply until it's posted) */}
        {!isProjectArchived && !isScheduled && (
        <div className={cn('border-t border-grey-200 pt-4 mt-4 no-print', overlay && 'flex-shrink-0')}>
          {/* Collapsed: one tap target, and nothing else competing for the
              screen. Rendered only when closed, so the editor keeps its state
              on desktop and after the first open. */}
          {!replyOpen && (
            <button
              type="button"
              onClick={() => setReplyOpen(true)}
              className="flex w-full items-center gap-3 rounded-xl border border-grey-200 bg-grey-50 px-4 py-3 text-left transition-colors hover:bg-grey-100"
            >
              <Avatar src={user?.avatarUrl} name={user?.name || 'User'} size="sm" />
              <span className="text-sm text-grey-500">Write a reply…</span>
            </button>
          )}
          <form onSubmit={handleSubmitReply} className={cn(!replyOpen && 'hidden')}>
            {/* Minimise: reading is why you came. Phone only — on desktop the
                composer costs nothing to leave open. */}
            <div className="mb-2 flex justify-end sm:hidden">
              <button
                type="button"
                onClick={() => setReplyOpen(false)}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-grey-500 hover:bg-grey-100 hover:text-secondary"
              >
                <ChevronDown className="h-4 w-4" />
                Minimise
              </button>
            </div>
            <div className="flex gap-3">
              <Avatar
                src={user?.avatarUrl}
                name={user?.name || 'User'}
                size="md"
                className="hidden sm:flex"
              />
              <div className="flex-1 min-w-0">
                <RichTextEditor
                  content={replyContent}
                  onChange={(html) => { setReplyContent(html); replyDraft.write(html); }}
                  placeholder="Write a reply..."
                  minHeight="80px"
                  maxHeight="45vh"
                  compactToolbar
                  enableAttachments={!!organizationId}
                  stagedFiles={replyAttachments.stagedFiles}
                  onFilesAdded={replyAttachments.addFiles}
                  onFileRemoved={replyAttachments.removeFile}
                  onEmbedImage={replyAttachments.embedImage}
                  onDemoteEmbed={replyAttachments.addUploaded}
                  disabled={replyMutation.isPending}
                  enableMentions={true}
                  mentionableUsers={mentionableUsers}
                  mentionUsersLoading={mentionUsersLoading}
                  onMention={handleReplyMention}
                  enableEveryoneMention={true}
                  enableGifPicker={true}
                />

                {/* Notifyees selector */}
                <div className="mt-3 text-sm">
                  <div className="flex flex-wrap items-center gap-1 text-grey-600">
                    <Mail className="w-4 h-4" />
                    <span>
                      {selectedNotifyNames.length === 0 ? (
                        'No one will be emailed'
                      ) : (
                        <>
                          <span className="hidden sm:inline">Your reply will be emailed to: </span>
                          <span className="sm:hidden">Emails: </span>
                          <span className="text-secondary">
                            {selectedNotifyNames.length <= 2
                              ? selectedNotifyNames.join(' and ')
                              : `${selectedNotifyNames.slice(0, 2).join(', ')} and ${selectedNotifyNames.length - 2} more`}
                          </span>
                        </>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => setNotifyExpanded(!notifyExpanded)}
                      className="text-accent hover:underline ml-1 flex items-center gap-0.5"
                    >
                      (change)
                      {notifyExpanded ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )}
                    </button>
                    {/* Leave / rejoin lives with the other "who gets emailed"
                        controls. It used to sit in the header beside the
                        modal's close X and was read as "close" — clients left
                        threads they had never opened (IFER, 2026-10-21). */}
                    {user && (
                      <button
                        type="button"
                        onClick={() => selfParticipationMutation.mutate(!viewerHasLeft)}
                        disabled={selfParticipationMutation.isPending}
                        title={viewerHasLeft ? 'Get emailed about replies again' : 'Stop getting emailed about replies to this thread'}
                        className="ml-auto flex items-center gap-1 text-xs text-grey-500 hover:text-secondary hover:underline disabled:opacity-50"
                      >
                        {viewerHasLeft ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                        {viewerHasLeft ? 'You left this thread · Rejoin' : 'Leave thread'}
                      </button>
                    )}
                  </div>

                  {/* Expanded notifyees list */}
                  {notifyExpanded && (
                    <div className="mt-3 p-4 bg-grey-50 rounded-lg border border-grey-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-secondary">Email this reply to people on the project:</span>
                        <div className="flex gap-2 text-xs">
                          <button
                            type="button"
                            onClick={selectAllNotifyUsers}
                            className="text-accent hover:underline"
                          >
                            Select all
                          </button>
                          <span className="text-grey-400">|</span>
                          <button
                            type="button"
                            onClick={selectNoneNotifyUsers}
                            className="text-accent hover:underline"
                          >
                            Select none
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 max-h-48 overflow-y-auto">
                        {projectMembers
                          .filter((m) => m.userId !== user?.id)
                          .map((member) => {
                            const canReceive = member.canReceiveDiscussionEmails !== false;
                            const isClient = (member as any).isClient === true;
                            const isLockedParticipant = lockedParticipantIds.has(member.userId);
                            const hasLeft = optedOutUserIds.includes(member.userId);
                            // Agency members may take a participant off or put a leaver back;
                            // everyone else sees those rows fixed.
                            const isDisabled = !canReceive || ((isLockedParticipant || hasLeft) && !isAgencyMember);
                            return (
                              <label
                                key={member.userId}
                                className={cn(
                                  "flex items-center gap-1.5 py-1 rounded",
                                  !isDisabled ? "cursor-pointer hover:bg-grey-100" : "cursor-not-allowed",
                                  !canReceive && "opacity-50"
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={notifyUserIds.includes(member.userId) && canReceive}
                                  onChange={() => toggleNotifyUser(member.userId)}
                                  disabled={isDisabled}
                                  className="w-3.5 h-3.5 rounded border-grey-300 text-accent focus:ring-accent disabled:opacity-70"
                                />
                                <span className={cn("truncate text-sm", !canReceive && "text-grey-400")}>
                                  {member.user.name}
                                </span>
                                {hasLeft && canReceive && (
                                  <span className="text-[10px] text-grey-400">(left thread)</span>
                                )}
                                {isLockedParticipant && canReceive && (
                                  <span className="text-[10px] text-grey-400">(participant)</span>
                                )}
                                {isClient && !isLockedParticipant && !hasLeft && canReceive && (
                                  <span className="text-[10px] text-grey-400">(client)</span>
                                )}
                                {!canReceive && (
                                  <span className="text-[10px] text-grey-400">(off)</span>
                                )}
                              </label>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end mt-3">
                  <Button
                    type="submit"
                    disabled={(!replyContent.trim() || replyContent === '<p></p>') && !replyAttachments.hasFiles}
                    isLoading={replyMutation.isPending || replyAttachments.isUploading}
                  >
                    <Send className="w-4 h-4" />
                    Reply
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
        )}
      </Card>

      {/* Agency admin: create a linked task from this discussion */}
      {isAgencyAdminViewer && projectId && threadId && (
        <CreateTaskFromDiscussionModal
          isOpen={showCreateTask}
          onClose={() => setShowCreateTask(false)}
          threadId={threadId}
          projectId={projectId}
        />
      )}
    </div>
    </div>
  );
}
